import { Dispatch, SetStateAction, SyntheticEvent } from 'react';
import { Box, Button, FormControl, FormControlLabel, Grid, Radio, RadioGroup, Typography } from '@mui/material';

import { header, organization, car, driver, buttonName } from '../../../common/const/commonConst';
import { searchButton } from '../../../common/const/componentSize';
import { SearchCriteriaType } from '../../../common/type/SearchCriteriaType';
import { MasterInfo } from '../../../common/type/MasterInfoType';
import { SearchAutocomplete, SearchStyledAutocomplete } from '../../../common/component/parts/SearchAutocomplete';
import { useStateSearchKey } from '../../../common/component/hooks/useStateSearchKey';
import { SelectedOffsegMasterInfoType } from '../../../common/component/hooks/useStateSelectedOffsegMasterInfo';
import { SelectedG500LiteMasterInfoType } from '../../../common/component/hooks/useStateSelectedG500LiteMasterInfo';

import { search } from '../../const/currentCarPlacePageSize';
import theme from '../../../theme';

type PropsType = {
	masterInfo: MasterInfo;
	selectedOffsegMasterInfo: SelectedOffsegMasterInfoType;
	setSelectedOffsegMasterInfo: Dispatch<SetStateAction<SelectedOffsegMasterInfoType>>;
	selectedG500LiteMasterInfo: SelectedG500LiteMasterInfoType;
	onChangeOffsegOrganization: (event: SyntheticEvent<Element, Event>, value: SearchCriteriaType | null) => void;
	onChangeOffsegCar: (event: SyntheticEvent<Element, Event>, value: SearchCriteriaType | null) => void;
	onChangeOffsegDriver: (event: SyntheticEvent<Element, Event>, value: SearchCriteriaType | null) => void;
	onChangeG500LiteOrganization: (event: SyntheticEvent<Element, Event>, value: SearchCriteriaType | null) => void;
	onChangeG500LiteCar: (event: SyntheticEvent<Element, Event>, value: SearchCriteriaType | null) => void;
	onClickSearch: () => Promise<void>;
};

/**
 * 車両現在位置一覧検索
 */
export const CurrentCarPlaceSearch = (props: PropsType) => {
	const {
		masterInfo,
		selectedOffsegMasterInfo,
		setSelectedOffsegMasterInfo,
		selectedG500LiteMasterInfo,
		onChangeOffsegOrganization,
		onChangeOffsegCar,
		onChangeOffsegDriver,
		onChangeG500LiteOrganization,
		onChangeG500LiteCar,
		onClickSearch,
	} = props;

	const { isCarSelectedAsKey, onChangeOffsegSearchKey } = useStateSearchKey({ selectedOffsegMasterInfo, setSelectedOffsegMasterInfo }); // Offsegの検索キー（車両 or ドライバー）の状態管理用カスタムフック

	return (
		<Box display="flex">
			<Grid container direction="column" justifyContent="center" alignItems="center" spacing={1.5}>
				<Grid item>
					<Grid container direction="row" justifyContent="space-between" alignItems="center">
						<Grid item xs={6}>
							<Typography
								role="lable"
								aria-label={header.offseg}
								position="relative"
								sx={{ fontSize: 17, color: theme.palette.primary.dark, width: search.halfWidth }}
							>
								{header.offseg}
							</Typography>
						</Grid>
						<Grid item xs={6}>
							{/* 車両/ドライバーの選択ボタン */}
							<FormControl sx={{ width: search.halfWidth, alignItems: 'flex-end' }}>
								<RadioGroup row defaultValue={car.id} onChange={(e, value) => onChangeOffsegSearchKey(e, value)}>
									<FormControlLabel
										value={car.id}
										control={<Radio id="carRadio" size="small" />}
										label={<Typography sx={{ fontSize: 15, color: theme.palette.common.black }}>{car.label}</Typography>}
									/>
									<FormControlLabel
										value={driver.id}
										control={<Radio id="driverRadio" size="small" />}
										label={<Typography sx={{ fontSize: 15, color: theme.palette.common.black }}>{driver.label}</Typography>}
									/>
								</RadioGroup>
							</FormControl>
						</Grid>
					</Grid>
				</Grid>
				<Grid item>
					{/* Offseg組織 */}
					<FormControl>
						<SearchAutocomplete
							id="offsegOrganization"
							options={masterInfo.offsegOrganizationInfoList.map((item) => ({
								id: item.organizationId,
								label: item.organizationName,
							}))}
							label={organization.label}
							value={selectedOffsegMasterInfo.selectedOffsegOrganizationInfo}
							onChange={(e, value) => onChangeOffsegOrganization(e, value)}
						/>
					</FormControl>
				</Grid>
				<Grid item>
					{/* Offseg車両 */}
					<FormControl>
						{isCarSelectedAsKey && (
							<SearchAutocomplete
								id="offsegCar"
								options={masterInfo.offsegCarInfoList.map((item) => ({ id: item.carId, label: item.carCode }))}
								label={car.label}
								value={selectedOffsegMasterInfo.selectedOffsegCarInfo}
								onChange={(e, value) => onChangeOffsegCar(e, value)}
							/>
						)}
						{!isCarSelectedAsKey && <SearchStyledAutocomplete label={car.label} />}
					</FormControl>
				</Grid>
				<Grid item>
					{/* Offsegドライバー */}
					<FormControl>
						{!isCarSelectedAsKey && (
							<SearchAutocomplete
								id="offsegDriver"
								options={masterInfo.offsegDriverInfoList.map((item) => ({ id: item.driverId, label: item.driverName }))}
								label={driver.label}
								value={selectedOffsegMasterInfo.selectedOffsegDriverInfo}
								onChange={(e, value) => onChangeOffsegDriver(e, value)}
							/>
						)}
						{isCarSelectedAsKey && <SearchStyledAutocomplete label={driver.label} />}
					</FormControl>
				</Grid>
				<Grid item>
					<Typography
						role="lable"
						aria-label={header.g500Lite}
						position="relative"
						sx={{ fontSize: 17, color: theme.palette.primary.dark, width: search.width }}
					>
						{header.g500Lite}
					</Typography>
				</Grid>
				<Grid item>
					{/* G500Lite組織 */}
					<FormControl>
						<SearchAutocomplete
							id="g500LiteOrganization"
							options={masterInfo.g500LiteOrganizationInfoList.map((item) => ({
								id: item.organizationId,
								label: item.organizationName,
							}))}
							label={organization.label}
							value={selectedG500LiteMasterInfo.selectedG500LiteOrganizationInfo}
							onChange={(e, value) => onChangeG500LiteOrganization(e, value)}
						/>
					</FormControl>
				</Grid>
				<Grid item>
					{/* G500Lite車両 */}
					<FormControl>
						<SearchAutocomplete
							id="g500LiteCar"
							options={masterInfo.g500LiteCarInfoList.map((item) => ({ id: item.carId, label: item.carCode }))}
							label={car.label}
							value={selectedG500LiteMasterInfo.selectedG500LiteCarInfo}
							onChange={(e, value) => onChangeG500LiteCar(e, value)}
						/>
					</FormControl>
				</Grid>
				<Grid item>
					{/* 検索ボタン */}
					<Box position="relative" left="197px">
						<Button id="searchButton" variant="contained" onClick={onClickSearch} sx={{ width: searchButton.width }}>
							<Typography sx={{ fontSize: 15, color: theme.palette.common.white }}>{buttonName.search}</Typography>
						</Button>
					</Box>
				</Grid>
			</Grid>
		</Box>
	);
};
